import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AvailabilityEdit, DayAvailability, LocalDate, RequestState, TimeWindow } from '@kronos/domain'
import { repository } from '../../data'
import type { MySchedule } from '../../data/schedule'
import type { Tone } from '../primitives'

/**
 * What every schedule screen shares: the query keys, the one save, and the
 * few words a state is shown as. The *rules* are in
 * `packages/domain/src/schedule.ts`; this is only how they read.
 */

export const MINE_KEY = ['schedule', 'mine'] as const
export const teamKey = (from: string | null): readonly unknown[] => ['schedule', 'team', from]
export const TODAY_KEY = ['schedule', 'today'] as const

/** After any request changes: both sides' lists, and both Today blocks. */
export function useScheduleChanged(): () => void {
  const queryClient = useQueryClient()
  return () => void queryClient.invalidateQueries({ queryKey: ['schedule'] })
}

/**
 * Save some of my days. The answer *is* the new schedule, so it replaces the
 * cached one directly rather than costing a second round trip.
 */
export function useSaveAvailability(onError?: (e: unknown) => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (days: readonly AvailabilityEdit[]) => repository.setAvailability(days),
    onSuccess: (mine: MySchedule) => {
      queryClient.setQueryData(MINE_KEY, mine)
      void queryClient.invalidateQueries({ queryKey: ['schedule', 'team'] })
      void queryClient.invalidateQueries({ queryKey: TODAY_KEY })
    },
    onError: (e: unknown) => onError?.(e),
  })
}

export const STATE_LABEL: Readonly<Record<RequestState, string>> = {
  pending: 'Asked',
  accepted: 'Coming in',
  declined: 'Declined',
  cancelled: 'Cancelled',
}

export const STATE_TONE: Readonly<Record<RequestState, Tone>> = {
  pending: 'warning',
  accepted: 'healthy',
  declined: 'neutral',
  cancelled: 'neutral',
}

/** `9–17`, `8:30–15` — for a grid cell, where `09:00–17:00` does not fit. */
export function shortWindow(w: TimeWindow | { from: string; to: string }): string {
  const short = (t: string): string => t.replace(/^0/, '').replace(/:00$/, '')
  return `${short(w.from)}–${short(w.to)}`
}

/** "Today", "Tomorrow", or nothing. */
export function relativeDay(date: LocalDate, today: LocalDate, tomorrow: LocalDate): string | null {
  return date === today ? 'Today' : date === tomorrow ? 'Tomorrow' : null
}

export function availabilityText(a: DayAvailability | null): string {
  if (!a) return ''
  return a.window ? `${a.window.from}–${a.window.to}` : 'Off'
}
